import { useCallback, useState } from "react";

import { api } from "../api.js";
import { ChartCard } from "../components/ChartCard.js";
import { BarChart, type BarChartItem } from "../components/charts/BarChart.js";
import { DayCapacityMixCard } from "../components/dashboard/DayCapacityMixCard.js";
import { ErrorsPerTableCard } from "../components/dashboard/ErrorsPerTableCard.js";
import {
  HealthMinuteExplorer,
  type HealthMinuteExplorerTarget,
} from "../components/dashboard/HealthMinuteExplorer.js";
import { IssuesByDayCard } from "../components/dashboard/IssuesByDayCard.js";
import { UptimeBreakdownPerDayCard } from "../components/dashboard/UptimeBreakdownPerDayCard.js";
import { UptimeByDayCard } from "../components/dashboard/UptimeByDayCard.js";
import { UptimeByTableCard } from "../components/dashboard/UptimeByTableCard.js";
import { WifiByTableCard } from "../components/dashboard/WifiByTableCard.js";
import { KpiRow } from "../components/KpiRow.js";
import type { ChartTableSelection } from "../lib/chartTables.js";
import { localHourNow } from "../lib/dashboardTime.js";
import { DEVICE_HEAT_BUCKETS } from "../lib/deviceHeatBuckets.js";
import { useReportingTimezone } from "../lib/ReportingTimezoneContext.js";
import { useSharedChartTables } from "../lib/useSharedChartTables.js";
import type { FilterState, HealthData, OpenRawEventsHandler } from "../types.js";

function fmtPct(n: number): string {
  return `${Math.round(n * 100)}%`;
}

function formatHoursLabel(hours: number): string {
  const t = Math.max(0, hours).toFixed(1);
  return t.endsWith(".0") ? t.slice(0, -2) : t;
}

/** Short string for chart axis (Unicode ellipsis). */
function squeeze(s: string, max: number): string {
  const t = s.trim();
  if (!t) return "—";
  return t.length <= max ? t : `${t.slice(0, Math.max(1, max - 1))}…`;
}

function topIssueBarLabel(issue: {
  severity: "warning" | "error";
  issueType: string;
  scope: string;
}): string {
  const icon = issue.severity === "warning" ? "⚠" : "⛔";
  const scope = issue.scope.trim();
  if (scope) {
    return `${icon} ${squeeze(issue.issueType, 11)} · ${squeeze(scope, 12)}`;
  }
  return `${icon} ${squeeze(issue.issueType, 20)}`;
}

interface HealthPanelProps {
  data: HealthData | null;
  filters: FilterState;
  onOpenRawEvents: OpenRawEventsHandler;
  chartTables: ChartTableSelection;
  onChartTablesChange: (next: ChartTableSelection) => void;
  chartFullWeeks: boolean;
  onChartFullWeeksChange: (next: boolean) => void;
}

export function HealthPanel({
  data,
  filters,
  onOpenRawEvents,
  chartTables,
  onChartTablesChange,
  chartFullWeeks,
  onChartFullWeeksChange,
}: HealthPanelProps) {
  const tables = useSharedChartTables(
    data?.devices ?? [],
    chartTables,
    onChartTablesChange,
    filters,
  );
  const [exportingClientErrors, setExportingClientErrors] = useState(false);
  const [minuteExplorer, setMinuteExplorer] = useState<HealthMinuteExplorerTarget | null>(null);
  const reportingTz = useReportingTimezone();

  const downloadClientErrorEventsJson = useCallback(async () => {
    setExportingClientErrors(true);
    try {
      await api.downloadHealthClientErrorEventsJson(filters);
    } catch (e) {
      window.alert(e instanceof Error ? e.message : "Download failed");
    } finally {
      setExportingClientErrors(false);
    }
  }, [filters]);

  if (!data) {
    return <p className="py-5 text-[13px] text-text-secondary">No data available</p>;
  }
  const localHourNowIndex = localHourNow(reportingTz);
  const warningCount = data.topIssues
    .filter((issue) => issue.severity === "warning")
    .reduce((sum, issue) => sum + issue.count, 0);
  const errorCount = data.topIssues
    .filter((issue) => issue.severity === "error")
    .reduce((sum, issue) => sum + issue.count, 0);

  const warn80BucketCount = data.tempBuckets[5] ?? 0;
  const veryHighBucketCount = data.tempBuckets[6] ?? 0;
  const showWarnCallout = warn80BucketCount > 0 || (data.avgTemp >= 80 && data.avgTemp < 85);
  const showVeryHighCallout = veryHighBucketCount > 0 || data.avgTemp >= 85;

  let uptimeHoursTotal = 0;
  let hoursLost = 0;
  for (const row of data.dailyUptime) {
    uptimeHoursTotal += Math.max(0, row.uptimeHours);
    hoursLost += Math.max(0, row.unscheduledHours);
  }
  // Historical gaps are unknown, so they must not become lost hours when the schedule target grows.
  const measuredOperatingHours = uptimeHoursTotal + hoursLost;
  const hoursLostPct = measuredOperatingHours > 1e-6 ? hoursLost / measuredOperatingHours : 0;

  const heatBarData: BarChartItem[] = DEVICE_HEAT_BUCKETS.map((bucket, i) => ({
    label: bucket.label,
    value: data.tempBuckets[i] ?? 0,
    fill: bucket.fill,
  }));

  const openHeatBucket = (index: number) => {
    const bucket = DEVICE_HEAT_BUCKETS[index];
    if (!bucket) return;
    setMinuteExplorer({
      kind: "heat",
      title: `Heat minutes · ${bucket.label} °C`,
      tempCelsiusMin: bucket.tempCelsiusMin,
      tempCelsiusMaxExclusive: bucket.tempCelsiusMaxExclusive,
      deviceId: filters.deviceId !== "all" ? filters.deviceId : undefined,
    });
  };

  const cutoverLabel = (data.heatCutoverIso ?? "2026-10-13T00:00:00.000Z").slice(0, 10);

  return (
    <>
      <KpiRow
        items={[
          { name: "Events in scope", value: String(data.eventCount), sub: "filtered by metrics" },
          {
            name: "Hours Lost",
            value: `${formatHoursLabel(hoursLost)}h (${fmtPct(hoursLostPct)})`,
            sub: "no signal; % of measured hours outside scheduled downtime",
          },
          {
            name: "Uptime Hours",
            value: `${formatHoursLabel(uptimeHoursTotal)}h`,
            sub: "fleet additive in filter scope",
          },
          {
            name: "Avg device heat",
            value: `${data.avgTemp.toFixed(1)} C`,
            sub: `telemetry °C from ${cutoverLabel} (incl. sleep)`,
          },
          {
            name: "Not measured",
            value: fmtPct(data.unmeasuredPct),
            sub: "share of table-hours without historical coverage",
          },
        ]}
      />

      <div className="grid grid-cols-1 gap-[10px] xl:grid-cols-2">
        <UptimeByDayCard
          dailyUptimeByDevice={data.dailyUptimeByDevice}
          dailyUptime={data.dailyUptime}
          lookbackDailyUptime={data.lookbackDailyUptime}
          lookbackDailyUptimeByDevice={data.lookbackDailyUptimeByDevice}
          devices={data.devices}
          filters={filters}
          selectedDeviceIds={tables.selectedIds}
          onSelectedDeviceIdsChange={tables.onSelectedIdsChange}
          externallyReconciled
          fullWeeks={chartFullWeeks}
          onFullWeeksChange={onChartFullWeeksChange}
        />

        <UptimeBreakdownPerDayCard
          dailyUptimeByDevice={data.dailyUptimeByDevice}
          devices={data.devices}
          filters={filters}
        />

        <UptimeByTableCard dailyUptimeByDevice={data.dailyUptimeByDevice} />

        <DayCapacityMixCard dailyUptime={data.dailyUptime} tableCount={data.tableCount} />

        <div className="xl:col-span-2">
          <WifiByTableCard
            filters={filters}
            chartTables={chartTables}
            onOpenWifiMinutes={(deviceId, deviceLabel, quality) =>
              setMinuteExplorer({
                kind: quality === "good" ? "wifi_good" : "wifi_bad",
                deviceId,
                deviceLabel,
                title: `${quality === "good" ? "Good" : "Bad"} Wi-Fi minutes · ${deviceLabel}`,
              })
            }
          />
        </div>

        <HealthMinuteExplorer
          filters={filters}
          target={minuteExplorer}
          onClose={() => setMinuteExplorer(null)}
        />

        <ChartCard
          title="Device Heat Minutes by Bucket"
          criteria={`Source: device_telemetry_minutes.soc_temp_celsius (one value per device per minute). Game filter does not apply. Sleep minutes are included (no scheduled-downtime exclusion).

Cutover: only minutes with minute_start ≥ ${cutoverLabel} (UTC ${data.heatCutoverIso ?? "2026-10-13T00:00:00.000Z"}). That is when telemetry minutes started carrying soc_temp_celsius next to Wi-Fi/RSSI. Before that, heat is empty here — device_temperature / device_temp_sample events are no longer used for this chart (games#295 can stop posting them any time after the cutover).

Y-axis: count(*) per band: <55, 55–60, 60–65, 65–70, 70–80, 80–85, 85+ °C.

Bar click: opens the Health minute list for that °C band (not Raw Events).`}
          note={`Telemetry °C minutes from ${cutoverLabel}. 80–85°C yellow; 85°C+ red. Click a bar for the minute list.`}
        >
          {showWarnCallout && (
            <div
              className="mb-2 flex items-center gap-2 rounded-lg border border-[#8a6a1a] bg-[#3a3010]/90 px-3 py-2 text-sm text-[#ffe8a8]"
              role="status"
            >
              <span className="text-lg leading-none" aria-hidden>
                ⚠️
              </span>
              <span className="font-semibold">High temp warning</span>
              <span className="text-[12px] text-[#e8d48a]">
                {warn80BucketCount > 0
                  ? `${Math.round(warn80BucketCount)} sample(s) at 80–85°C in range.`
                  : `Average device heat is ${data.avgTemp.toFixed(1)}°C (80–85°C band).`}
              </span>
            </div>
          )}
          {showVeryHighCallout && (
            <div
              className="mb-2 flex items-center gap-2 rounded-lg border border-[#8b3a3a] bg-[#3a1518]/90 px-3 py-2 text-sm text-[#ffc9c9]"
              role="alert"
            >
              <span className="text-lg leading-none" aria-hidden>
                🌡️
              </span>
              <span className="font-semibold">Very high temp detected</span>
              <span className="text-[12px] text-[#e8a8a8]">
                {veryHighBucketCount > 0
                  ? `${Math.round(veryHighBucketCount)} sample(s) at ≥85°C in range.`
                  : `Average device heat is ${data.avgTemp.toFixed(1)}°C (≥85°C threshold).`}
              </span>
            </div>
          )}
          <BarChart
            verbatimAxisLabels
            axisLabelMaxChars={6}
            valueLabelAxisTitle="Minutes"
            axisLabelAxisTitle="°C"
            data={heatBarData}
            barTooltip={(item, i) => {
              const bucket = DEVICE_HEAT_BUCKETS[i];
              const band = bucket?.label ?? item.label;
              return `Click: minute list for ${band}°C from device_telemetry_minutes (not Raw Events).`;
            }}
            onBarClick={(_item, i) => openHeatBucket(i)}
          />
        </ChartCard>

        <IssuesByDayCard
          rows={data.issuesByDay}
          devices={data.devices}
          dailyUptimeByDevice={data.dailyUptimeByDevice}
          issuesByDeviceDay={data.issuesByDeviceDay}
          tableCount={data.tableCount}
          chartTables={chartTables}
          onChartTablesChange={onChartTablesChange}
          fullWeeks={chartFullWeeks}
          onFullWeeksChange={onChartFullWeeksChange}
          filters={filters}
          onOpenRawEvents={onOpenRawEvents}
        />

        <ChartCard
          title="Top Issues (Warnings + Errors)"
          criteria={`Source: device_events where event_name in ('client_error','console_warning','console_error').

Grouped by: coalesce(properties->>'issue_type', properties->>'error_type'), properties->>'scope', event_name, and severity:
• warning if client_error with properties.log_level warning, or console_warning
• else error

Y-axis: count(*) per group (top 20).

Bar labels: severity icon, coalesced issue/error type, and scope when present (verbatim axis; trimmed in SVG).

Download JSON: GET /api/analytics/health/client-error-events-export — up to 200k rows (event_name = client_error only) with metadata in the file.`}
          note="Warning = client_error with log_level warning, or console_warning. Error = everything else in scope."
          titleRight={
            <>
              <button
                type="button"
                disabled={exportingClientErrors}
                className="rounded border border-border-subtle bg-panel-2 px-2 py-1 text-xs text-text-primary hover:bg-[#223455] disabled:cursor-not-allowed disabled:opacity-40"
                title="Download all client_error events in scope as JSON"
                aria-label="Download all client_error events in scope as JSON"
                onClick={() => void downloadClientErrorEventsJson()}
              >
                {exportingClientErrors ? "…" : "⬇"}
              </button>
              <button
                type="button"
                className="rounded border border-border-subtle bg-panel-2 px-2 py-1 text-xs text-text-primary hover:bg-[#223455]"
                onClick={(e) =>
                  onOpenRawEvents({ eventName: "client_error", clientErrorBucket: "warning" }, e)
                }
                onAuxClick={(e) => {
                  if (e.button !== 1) return;
                  onOpenRawEvents({ eventName: "client_error", clientErrorBucket: "warning" }, e);
                }}
                title="Open client_error rows with log_level warning in Raw Events (middle-click / Ctrl/Cmd+click: new tab)"
              >
                ⚠ {warningCount}
              </button>
              <button
                type="button"
                className="rounded border border-border-subtle bg-panel-2 px-2 py-1 text-xs text-text-primary hover:bg-[#223455]"
                onClick={(e) =>
                  onOpenRawEvents({ eventName: "client_error", clientErrorBucket: "error" }, e)
                }
                onAuxClick={(e) => {
                  if (e.button !== 1) return;
                  onOpenRawEvents({ eventName: "client_error", clientErrorBucket: "error" }, e);
                }}
                title="Open client_error rows that are not log_level warning in Raw Events (middle-click / Ctrl/Cmd+click: new tab)"
              >
                ⛔ {errorCount}
              </button>
            </>
          }
        >
          <BarChart
            verbatimAxisLabels
            axisLabelMaxChars={30}
            data={data.topIssues.map((issue) => {
              return {
                label: topIssueBarLabel(issue),
                value: issue.count,
                fill: issue.severity === "warning" ? "var(--color-warn)" : "var(--color-err)",
              };
            })}
            barTooltip={(_, i) => {
              const issue = data.topIssues[i];
              if (!issue) return "Click: open Raw Events.";
              const icon = issue.severity === "warning" ? "⚠" : "⛔";
              const type = issue.issueType.trim() || "(no issue_type)";
              const sc = issue.scope.trim();
              const scopeBit = sc ? ` · scope ${sc}` : "";
              if (issue.eventName === "client_error") {
                return `${icon} ${issue.eventName} · ${type}${scopeBit} — count ${issue.count}. Click: Raw Events → client_error (${issue.severity}); substring filter uses type and scope when set. Middle-click / Ctrl/Cmd+click: new tab.`;
              }
              return `${icon} ${issue.eventName} · ${type}${scopeBit} — count ${issue.count}. Click: Raw Events → ${issue.eventName}; substring filter uses type and scope when set. Middle-click / Ctrl/Cmd+click: new tab.`;
            }}
            onBarClick={(_, i, _si, e) => {
              const issue = data.topIssues[i];
              if (!issue) return;
              const typeNarrow = issue.issueType.trim();
              const scopeNarrow = issue.scope.trim();
              const propertiesContains =
                [typeNarrow, scopeNarrow].filter(Boolean).join(" ") || undefined;
              if (issue.eventName === "client_error") {
                onOpenRawEvents(
                  {
                    eventName: "client_error",
                    clientErrorBucket: issue.severity === "warning" ? "warning" : "error",
                    propertiesContains,
                  },
                  e,
                );
              } else {
                onOpenRawEvents(
                  {
                    eventName: issue.eventName,
                    propertiesContains,
                  },
                  e,
                );
              }
            }}
          />
          <p className="mt-2 text-[11px] leading-relaxed text-text-secondary">
            You can trigger test async warnings by pressing 4 game icons, and errors with 5 game
            icons.
          </p>
        </ChartCard>

        <ChartCard
          title="Issues by Hour"
          criteria={`Source: device_events where event_name in ('client_error','console_warning','console_error').

X-axis: extract(hour from client_timestamp AT TIME ZONE reporting timezone).

Y-axis: count(*) per local hour bucket (0–23).

Raw Events has no hour-of-day filter; bars are not clickable for drill-down.`}
          note="Helps identify reporting-timezone-specific platform stress."
        >
          <BarChart
            data={data.hourlyIssueCounts.map((v, i) => ({
              label: String(i),
              value: v,
            }))}
            compact
            highlightIndex={localHourNowIndex}
            highlightLabel="Now (local)"
            barTooltip={(item) =>
              `Local hour ${item.label}: ${Math.round(item.value)} events. No matching Raw Events hour filter from this chart.`
            }
          />
        </ChartCard>

        <ErrorsPerTableCard
          devices={data.devices}
          issuesByDeviceDay={data.issuesByDeviceDay}
          chartTables={chartTables}
          onChartTablesChange={onChartTablesChange}
          filters={filters}
          onOpenRawEvents={onOpenRawEvents}
        />
      </div>
    </>
  );
}
